import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserPlus, Search, Filter, Mail, Copy, RefreshCw, XCircle, Clock,
  CheckCircle2, AlertTriangle, Loader2, MoreVertical, Send, Users, Link2,
  ChevronDown, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem('d3_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   color: 'text-amber-400',   bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Clock },
  accepted:  { label: 'Accepted',  color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'text-zinc-500',    bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', icon: XCircle },
  expired:   { label: 'Expired',   color: 'text-red-400',     bg: 'bg-red-500/10', border: 'border-red-500/20', icon: AlertTriangle },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${cfg.bg} ${cfg.color} border ${cfg.border}`}
      data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const isExpired = (invite) => {
  if (invite.status !== 'pending') return false;
  if (!invite.expiresAt) return false;
  return new Date(invite.expiresAt) < new Date();
};

// ── Create Invite Modal ──────────────────────────────────────
const CreateInviteModal = ({ isOpen, onClose, onCreated }) => {
  const [form, setForm] = useState({ email: '', name: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (isOpen) { setForm({ email: '', name: '', message: '' }); setResult(null); }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invites`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ email: form.email.trim(), name: form.name.trim(), message: form.message.trim() }),
      });
      if (res.ok) {
        const inv = await res.json();
        setResult(inv);
        onCreated?.(inv);
        toast.success('Invitation sent!');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create invitation');
      }
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  const copyLink = async (link) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Invite link copied!');
    } catch { toast.error('Failed to copy'); }
  };

  if (!isOpen) return null;
  const inviteLink = result?.token ? `${window.location.origin}/invite?token=${result.token}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-lg p-6 shadow-2xl"
        onClick={e => e.stopPropagation()} data-testid="create-invite-modal">
        {!result ? (
          <>
            <h2 className="text-base font-semibold text-white mb-1">Send Client Invitation</h2>
            <p className="text-xs text-zinc-500 mb-5">Invite a client to join your organization. They will receive an email with a secure registration link.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Email Address *</label>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  type="email" required placeholder="client@company.com"
                  className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                  data-testid="invite-email-input" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Full Name</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                  data-testid="invite-name-input" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Personal Message (Optional)</label>
                <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Looking forward to working together..."
                  className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40 resize-none" rows={2}
                  data-testid="invite-message-input" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Cancel</button>
                <button type="submit" disabled={loading || !form.email.trim()}
                  className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-40 flex items-center gap-2"
                  data-testid="send-invite-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Invitation
                </button>
              </div>
            </form>
          </>
        ) : (
          <div data-testid="invite-success">
            <div className="text-center mb-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="text-base font-semibold text-white">Invitation Sent!</h2>
              <p className="text-xs text-zinc-500 mt-1">An email has been sent to <span className="text-indigo-400">{result.email}</span></p>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Or share this link directly:</label>
              <div className="flex gap-2">
                <input value={inviteLink} readOnly onClick={(e) => e.target.select()}
                  className="flex-1 px-3 py-2.5 text-xs bg-white/5 border border-white/10 rounded-lg text-zinc-300 font-mono truncate" />
                <button type="button" onClick={() => copyLink(inviteLink)}
                  className="shrink-0 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white rounded-lg cursor-pointer"
                  data-testid="copy-invite-link">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={onClose} className="px-5 py-2 text-sm font-medium bg-white/5 hover:bg-white/10 text-white rounded-lg" data-testid="done-btn">Done</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ── Row Actions Dropdown ──────────────────────────────────────
const RowActions = ({ invite, onResend, onCancel, onCopy }) => {
  const [open, setOpen] = useState(false);
  const pending = invite.status === 'pending' && !isExpired(invite);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-white transition-colors"
        data-testid={`actions-btn-${invite.id}`}>
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-50 bg-[#0d1221] border border-white/10 rounded-xl shadow-2xl py-1.5 w-44" data-testid={`actions-menu-${invite.id}`}>
            {pending && (
              <>
                <button onClick={() => { onResend(invite); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
                  data-testid={`resend-btn-${invite.id}`}>
                  <RefreshCw className="w-3.5 h-3.5" /> Resend Email
                </button>
                <button onClick={() => { onCopy(invite); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
                  data-testid={`copy-link-btn-${invite.id}`}>
                  <Link2 className="w-3.5 h-3.5" /> Copy Link
                </button>
                <div className="border-t border-white/5 my-1" />
                <button onClick={() => { onCancel(invite); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-400 hover:bg-red-500/5 hover:text-red-300 transition-colors"
                  data-testid={`cancel-btn-${invite.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Cancel Invite
                </button>
              </>
            )}
            {!pending && (
              <p className="px-3 py-2 text-xs text-zinc-600">No actions available</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── Main Invitations Screen ──────────────────────────────────
export default function InvitationsScreen() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchInvites = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API_URL}/api/invites${params}`, { headers: getHeaders() });
      if (res.ok) setInvites(await res.json());
    } catch { /* silent */ }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const filtered = invites.filter(inv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return inv.email?.toLowerCase().includes(q) || inv.name?.toLowerCase().includes(q);
  });

  // Mark expired invites visually
  const displayInvites = filtered.map(inv => ({
    ...inv,
    displayStatus: isExpired(inv) ? 'expired' : inv.status,
  }));

  const handleResend = async (invite) => {
    try {
      const res = await fetch(`${API_URL}/api/invites/${invite.id}/resend`, {
        method: 'POST', headers: getHeaders(),
      });
      if (res.ok) {
        toast.success(`Invitation resent to ${invite.email}`);
        fetchInvites();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to resend');
      }
    } catch { toast.error('Connection error'); }
  };

  const handleCancel = async (invite) => {
    try {
      const res = await fetch(`${API_URL}/api/invites/${invite.id}/cancel`, {
        method: 'POST', headers: getHeaders(),
      });
      if (res.ok) {
        toast.success(`Invitation to ${invite.email} cancelled`);
        fetchInvites();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to cancel');
      }
    } catch { toast.error('Connection error'); }
  };

  const handleCopy = async (invite) => {
    const link = `${window.location.origin}/invite?token=${invite.token}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Invite link copied!');
    } catch { toast.error('Failed to copy'); }
  };

  // Stats
  const stats = {
    total: invites.length,
    pending: invites.filter(i => i.status === 'pending' && !isExpired(i)).length,
    accepted: invites.filter(i => i.status === 'accepted').length,
    expired: invites.filter(i => isExpired(i)).length,
  };

  const filters = [
    { id: 'all', label: 'All', count: stats.total },
    { id: 'pending', label: 'Pending', count: stats.pending },
    { id: 'accepted', label: 'Accepted', count: stats.accepted },
    { id: 'cancelled', label: 'Cancelled', count: invites.filter(i => i.status === 'cancelled').length },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="invitations-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Client Invitations</h1>
          <p className="text-xs text-zinc-500 mt-1">Manage and track all client invitations to your organization</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors shadow-[0_0_20px_rgba(79,70,229,0.2)]"
          data-testid="new-invite-btn">
          <UserPlus className="w-4 h-4" /> New Invitation
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="stat-total">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Invites</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="stat-pending">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Pending</p>
          <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="stat-accepted">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Accepted</p>
          <p className="text-2xl font-bold text-emerald-400">{stats.accepted}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="stat-expired">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Expired</p>
          <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex gap-1.5">
          {filters.map(f => (
            <button key={f.id} onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                statusFilter === f.id
                  ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5 border border-transparent'
              }`}
              data-testid={`filter-${f.id}`}>
              {f.label} <span className="ml-1 text-[10px] opacity-60">{f.count}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by email or name..."
            className="pl-9 pr-4 py-2 w-64 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
            data-testid="search-invites" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          </div>
        ) : displayInvites.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-7 h-7 text-zinc-600" />
            </div>
            <p className="text-sm text-zinc-500 mb-1">{searchQuery ? 'No matching invitations' : 'No invitations yet'}</p>
            <p className="text-xs text-zinc-600 mb-4">Send your first client invitation to get started</p>
            {!searchQuery && (
              <button onClick={() => setShowCreate(true)}
                className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg" data-testid="empty-invite-btn">
                <UserPlus className="w-4 h-4 inline mr-1.5" /> Send Invitation
              </button>
            )}
          </div>
        ) : (
          <table className="w-full" data-testid="invites-table">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Recipient</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Sent</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Expires</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Invited By</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayInvites.map((inv, i) => (
                <motion.tr key={inv.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                  data-testid={`invite-row-${inv.id}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm text-white font-medium">{inv.name || '—'}</p>
                      <p className="text-xs text-zinc-500">{inv.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={inv.displayStatus} />
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{formatDate(inv.createdAt)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{formatDate(inv.expiresAt)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{inv.invitedByName || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <RowActions invite={inv} onResend={handleResend} onCancel={handleCancel} onCopy={handleCopy} />
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      <CreateInviteModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => fetchInvites()}
      />
    </div>
  );
}
